// Module 3 / Slice 8 — station-facility admin screen.
//
// Read-only view of the station_facility_extension row attached to a
// single organization_unit. The unit id is supplied as a route param;
// upsert + remove flows ship as a drawer in Phase 4 follow-up
// (C-mod3-9 inline-edit + bulk-save).
//
// 404 (no extension yet, or unit not found) is rendered as a friendly
// "No facility extension on this unit." message rather than an alert,
// because absence is a normal state for any non-facility-capable unit.

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { organizationApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

export function StationFacilityScreen(): JSX.Element {
  const { t } = useI18n();
  const { unitId } = useParams({ from: "/authed/admin/organization/units/$unitId/station-facility" });

  const query = useQuery({
    queryKey: ["organization", "station-facility", unitId],
    queryFn: async () => {
      const response = await organizationApiClient.GET(
        "/organization/units/{unitId}/station-facility",
        { params: { path: { unitId } } },
      );
      if (response.response.status === 404) {
        return null;
      }
      if (!response.data) {
        throw new Error("organization_station_facility_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("organization.stationFacility.heading")}</h1>
      <p className="rc-help">{t("organization.stationFacility.description")}</p>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data === null && (
        <p>{t("organization.stationFacility.empty")}</p>
      )}
      {query.data && (
        <dl className="rc-detail-list">
          <dt>{t("organization.stationFacility.label.address1")}</dt>
          <dd>{query.data.addressLine1 ?? "\u2014"}</dd>
          <dt>{t("organization.stationFacility.label.phone")}</dt>
          <dd>{query.data.phone ?? "\u2014"}</dd>
          <dt>{t("organization.stationFacility.label.latLong")}</dt>
          <dd>
            {query.data.latitude !== null && query.data.latitude !== undefined ? (
              <code>
                {query.data.latitude}, {query.data.longitude}
              </code>
            ) : (
              "\u2014"
            )}
          </dd>
          <dt>{t("organization.stationFacility.label.stationType")}</dt>
          <dd>{query.data.stationTypeKey ?? "\u2014"}</dd>
          <dt>{t("organization.stationFacility.label.isVolunteer")}</dt>
          <dd>{query.data.isVolunteer ? "\u2714" : "\u2014"}</dd>
          <dt>{t("organization.stationFacility.label.jobCode")}</dt>
          <dd>{query.data.jobCode ?? "\u2014"}</dd>
        </dl>
      )}
    </div>
  );
}
